/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined } from '@ant-design/icons'
import { Button, Form, Tabs, notification } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useConfigurationProperties } from '@hooks'
import { useUser } from '@contexts'
import { CutoffTimeUtils, DeliveryNoteUtils } from '@utils'

import { AdminTicketForm, AssignationValues, DeliveryNote, TripTypeEnum } from '../../../types'
import { StyledModal } from '../../../components/StyledComponents'
import AddTicketFormAdmin from './AddTicketFormAdmin'
import { useGeneral } from '../../../contexts'

type TabValue = '1' | '2'

interface Props {
  onOk: (
    id: number | undefined,
    form: AdminTicketForm,
    resetForm: () => void,
    dates: Date[] | undefined,
    toApprove?: boolean | undefined,
  ) => void
  title: string
  isLoading: boolean
  onClose: (reset?: () => void) => void
  open?: boolean
}

const AddTicketModalAdmin: React.FC<Props> = ({ onClose, title, onOk, isLoading, open }) => {
  const { t } = useTranslation()
  const {
    assignmentTickets,
    carrierAvailabilityIsEnabled,
    maxAllowedDaysForSupplierRequest,
    cutoffTimeHour,
  } = useConfigurationProperties()
  const { setFormInstance } = useGeneral()
  const { state: user } = useUser()

  const [form] = Form.useForm()

  const [isWarning, setIsWarning] = useState<boolean>()
  const [assignValue, setAssignValue] = useState<AssignationValues>()
  const [dates, setDates] = useState<Date[]>()
  const [primaryButtonIsDisabled, setPrimaryButtonIsDisabled] = useState<boolean>()
  const [activeTab, setActiveTab] = useState<TabValue>()

  const roles = user?.roles.map(role => role.name) || []

  const handlePrimaryButtonDisabling = (value: boolean) => {
    setPrimaryButtonIsDisabled(!!value)
  }

  const resetModal = useCallback(() => {
    onClose()
    notification.destroy('validationError')
    setIsWarning(undefined)
    setAssignValue(defaultAssignValue)
    setDates(undefined)
    setPrimaryButtonIsDisabled(undefined)
  }, [onClose])

  const handleCloseModal = () => {
    resetModal()
  }

  const handleAfterModalClose = () => {
    setDates(undefined)
  }

  const defaultAssignValue = useMemo(
    () => (assignmentTickets === 'true' ? 'automatic' : 'manual'),
    [assignmentTickets],
  )
  const cutoffTime = useMemo(
    () =>
      typeof cutoffTimeHour === 'number' && !Number.isNaN(cutoffTimeHour)
        ? `${String(cutoffTimeHour).padStart(2, '0')}:00`
        : CutoffTimeUtils.getCutoffTime(),
    [cutoffTimeHour],
  )

  const proceed = useCallback(() => {
    if (dates?.length === undefined) {
      setIsWarning(true)
    }

    form.setFieldsValue({ datesInput: dates?.length ? true : undefined })

    form
      .validateFields()
      .then(values => {
        const deliveryNoteNumber = DeliveryNoteUtils.normalizeValue(values.number)
        const deliveryNote: DeliveryNote | undefined = deliveryNoteNumber
          ? {
              number: deliveryNoteNumber,
              ...(values.observation ? { observation: values.observation } : {}),
            }
          : undefined
        const isCfr = values.tripType === TripTypeEnum.CFR

        onOk(
          undefined,
          {
            ...(deliveryNote && { deliveryNote }),
            count: values.count,
            depotId: values.depotId,
            materialId: values.materialId,
            equipmentId: values.equipmentId,
            destinationId: values.destinationId,
            carrierId: values.carrierId,
            note: values.comments ? { description: values.comments } : undefined,
            documentNumberId: values.documentNumberId ?? undefined,
            type: assignValue,
            declinedLoad: values.declinedLoad,
            declinedLoadType: values.declinedLoadType,
            tripType: values.tripType,
            ticketSap:
              typeof values.ticketSap === 'string' && /^\d+$/.test(values.ticketSap)
                ? values.ticketSap
                : null,
            ...(!isCfr && { sapItemId: values.sapItemId }),
          },
          handleCloseModal,
          dates,
          false, // no ifToApproved en creación
        )
      })
      .catch(() => {
        notification.destroy('validationError')
        notification.error({
          message: t('globals.validation_error'),
          key: 'validationError',
        })
      })
  }, [dates, assignValue, form])

  useEffect(() => {
    if (isLoading) {
      setActiveTab(undefined)
    } else {
      setAssignValue(defaultAssignValue)
      handlePrimaryButtonDisabling(false)
      setActiveTab('1')
    }
  }, [isLoading])

  useEffect(() => {
    if (form) {
      setFormInstance(form)
    }
    return () => {
      setFormInstance(undefined)
    }
  }, [form])
  return (
    <StyledModal
      afterClose={handleAfterModalClose}
      destroyOnClose
      keyboard
      maskClosable
      className="create-modal admin-modal"
      open={open}
      onCancel={handleCloseModal}
      title={title}
      closeIcon={<CloseOutlined />}
      cancelText={t('suppliers_router.new_ticket_modal.cancelText')}
      footer={[
        <Button key="cancelar" onClick={handleCloseModal}>
          {t('suppliers_router.new_ticket_modal.cancelText')}
        </Button>,
        <Button
          type="primary"
          key="ok"
          onClick={proceed}
          disabled={!activeTab || primaryButtonIsDisabled}
        >
          {t('suppliers_router.new_ticket_modal.okText')}
        </Button>,
      ]}
    >
      <Tabs
        activeKey={activeTab}
        items={[
          {
            label: t('ticket_modal.tab_info'),
            key: '1',
            children: (
              <AddTicketFormAdmin
                handlePrimaryButtonDisabling={handlePrimaryButtonDisabling}
                dates={dates}
                setDates={setDates}
                roles={roles}
                isWarning={isWarning}
                setIsWarning={setIsWarning}
                ifToApproved={false}
                form={form}
                setAssignValue={setAssignValue}
                assignValue={assignValue}
                carrierAvailabilityIsEnabled={carrierAvailabilityIsEnabled}
                maxAllowedDaysForSupplierRequest={maxAllowedDaysForSupplierRequest}
                defaultAssignValue={defaultAssignValue}
                cutoffTime={cutoffTime}
              />
            ),
          },
        ]}
      />
    </StyledModal>
  )
}

export { AddTicketModalAdmin }
